/**
 * Professional Elements Editor Component
 * Provides UI for editing industry-standard publishing metadata and legal elements
 */

import React, { useState, useEffect } from 'react';
import { 
  ProfessionalElements, 
  generateCopyrightPage, 
  generateProductionCredits,
  generateCIPData,
  validateProfessionalElements,
  getDefaultProfessionalElements
} from '../utils/publishingElements';

interface ProfessionalElementsEditorProps {
  elements: ProfessionalElements;
  onChange: (elements: ProfessionalElements) => void;
  bookTitle: string;
  authorName: string;
  isOpen: boolean;
  onClose: () => void;
}

const ProfessionalElementsEditor: React.FC<ProfessionalElementsEditorProps> = ({
  elements,
  onChange,
  bookTitle,
  authorName,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<string>('publisher');
  const [localElements, setLocalElements] = useState<ProfessionalElements>(elements);
  const [preview, setPreview] = useState<string>('');
  const [validation, setValidation] = useState<any>(null);

  useEffect(() => {
    setLocalElements(elements);
  }, [elements]);

  useEffect(() => {
    if (isOpen) {
      const validationResult = validateProfessionalElements(localElements);
      setValidation(validationResult);
    }
  }, [localElements, isOpen]);

  const tabs = [
    { id: 'publisher', label: 'Publisher Info', icon: '🏢' },
    { id: 'legal', label: 'Legal Notices', icon: '⚖️' },
    { id: 'library', label: 'Library Data', icon: '📚' },
    { id: 'credits', label: 'Production Credits', icon: '👥' },
    { id: 'metadata', label: 'Metadata', icon: '📊' },
    { id: 'preview', label: 'Preview', icon: '👀' }
  ];

  const updateElements = (section: keyof ProfessionalElements, data: any) => {
    const updated = {
      ...localElements,
      [section]: { ...localElements[section], ...data }
    };
    setLocalElements(updated);
  };

  const updateNestedField = (section: keyof ProfessionalElements, field: string, value: any) => {
    const updated = {
      ...localElements,
      [section]: {
        ...localElements[section],
        [field]: value
      }
    };
    setLocalElements(updated);
  };

  const handleSave = () => {
    onChange(localElements);
    onClose();
  };

  const generatePreview = () => {
    switch (activeTab) {
      case 'preview':
        return generateCopyrightPage(localElements, bookTitle, authorName);
      default:
        return '';
    }
  };

  useEffect(() => {
    if (activeTab === 'preview') {
      setPreview(generatePreview());
    }
  }, [activeTab, localElements, bookTitle, authorName]);

  const renderPublisherTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Publisher Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Publisher Name *
          </label>
          <input
            type="text"
            value={localElements.publisherInfo.publisherName}
            onChange={(e) => updateNestedField('publisherInfo', 'publisherName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Your Publishing Company"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Imprint Name
          </label>
          <input
            type="text"
            value={localElements.publisherInfo.imprintName || ''}
            onChange={(e) => updateNestedField('publisherInfo', 'imprintName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Imprint or division name"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Publisher Address *
        </label>
        <textarea
          value={localElements.publisherInfo.publisherAddress}
          onChange={(e) => updateNestedField('publisherInfo', 'publisherAddress', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="123 Publishing Street&#10;City, State 12345&#10;Country"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Website
          </label>
          <input
            type="url"
            value={localElements.publisherInfo.website}
            onChange={(e) => updateNestedField('publisherInfo', 'website', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://www.yourpublisher.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Email
          </label>
          <input
            type="email"
            value={localElements.publisherInfo.email || ''}
            onChange={(e) => updateNestedField('publisherInfo', 'email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="info@yourpublisher.com"
          />
        </div>
      </div>
    </div>
  );

  const renderLegalTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Legal Notices</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Copyright Notice *
        </label>
        <input
          type="text"
          value={localElements.legalNotices.copyrightNotice}
          onChange={(e) => updateNestedField('legalNotices', 'copyrightNotice', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={`© ${new Date().getFullYear()} ${authorName}`}
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="allRightsReserved"
          checked={localElements.legalNotices.allRightsReserved}
          onChange={(e) => updateNestedField('legalNotices', 'allRightsReserved', e.target.checked)}
          className="h-4 w-4 text-blue-600 rounded"
        />
        <label htmlFor="allRightsReserved" className="text-sm text-gray-700">
          Include "All rights reserved" statement
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Trademark Notice
        </label>
        <input
          type="text"
          value={localElements.legalNotices.trademarkNotice || ''}
          onChange={(e) => updateNestedField('legalNotices', 'trademarkNotice', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Trademark information if applicable"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Disclaimers
        </label>
        <div className="space-y-2">
          {localElements.legalNotices.disclaimers.map((disclaimer, index) => (
            <div key={index} className="flex space-x-2">
              <textarea
                value={disclaimer}
                onChange={(e) => {
                  const updated = [...localElements.legalNotices.disclaimers];
                  updated[index] = e.target.value;
                  updateNestedField('legalNotices', 'disclaimers', updated);
                }}
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Disclaimer text"
              />
              <button
                onClick={() => {
                  const updated = localElements.legalNotices.disclaimers.filter((_, i) => i !== index);
                  updateNestedField('legalNotices', 'disclaimers', updated);
                }}
                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const updated = [...localElements.legalNotices.disclaimers, ''];
              updateNestedField('legalNotices', 'disclaimers', updated);
            }}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Add Disclaimer
          </button>
        </div>
      </div>
    </div>
  );

  const renderLibraryTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Library of Congress Data</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ISBN-13
          </label>
          <input
            type="text"
            value={localElements.libraryOfCongressData.isbn13 || ''}
            onChange={(e) => updateNestedField('libraryOfCongressData', 'isbn13', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="978-0-123456-78-9"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ISBN-10
          </label>
          <input
            type="text"
            value={localElements.libraryOfCongressData.isbn10 || ''}
            onChange={(e) => updateNestedField('libraryOfCongressData', 'isbn10', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0123456789"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            LCCN (Library of Congress Control Number)
          </label>
          <input
            type="text"
            value={localElements.libraryOfCongressData.lccn || ''}
            onChange={(e) => updateNestedField('libraryOfCongressData', 'lccn', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="2023123456"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dewey Decimal Classification
          </label>
          <input
            type="text"
            value={localElements.libraryOfCongressData.deweyDecimal}
            onChange={(e) => updateNestedField('libraryOfCongressData', 'deweyDecimal', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="813.6 (for fiction)"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subject Headings
        </label>
        <div className="space-y-2">
          {localElements.libraryOfCongressData.subjectHeadings.map((heading, index) => (
            <div key={index} className="flex space-x-2">
              <input
                type="text"
                value={heading}
                onChange={(e) => {
                  const updated = [...localElements.libraryOfCongressData.subjectHeadings];
                  updated[index] = e.target.value;
                  updateNestedField('libraryOfCongressData', 'subjectHeadings', updated);
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Subject heading"
              />
              <button
                onClick={() => {
                  const updated = localElements.libraryOfCongressData.subjectHeadings.filter((_, i) => i !== index);
                  updateNestedField('libraryOfCongressData', 'subjectHeadings', updated);
                }}
                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const updated = [...localElements.libraryOfCongressData.subjectHeadings, ''];
              updateNestedField('libraryOfCongressData', 'subjectHeadings', updated);
            }}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Add Subject Heading
          </button>
        </div>
      </div>
    </div>
  );

  const renderCreditsTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Production Credits</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Editor
          </label>
          <input
            type="text"
            value={localElements.productionCredits.editor || ''}
            onChange={(e) => updateNestedField('productionCredits', 'editor', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Editor name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cover Designer
          </label>
          <input
            type="text"
            value={localElements.productionCredits.coverDesigner || ''}
            onChange={(e) => updateNestedField('productionCredits', 'coverDesigner', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Cover designer name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Copy Editor
          </label>
          <input
            type="text"
            value={localElements.productionCredits.copyEditor || ''}
            onChange={(e) => updateNestedField('productionCredits', 'copyEditor', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Copy editor name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Illustrator
          </label>
          <input
            type="text"
            value={localElements.productionCredits.illustrator || ''}
            onChange={(e) => updateNestedField('productionCredits', 'illustrator', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Illustrator name"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Credits
        </label>
        <div className="space-y-2">
          {localElements.productionCredits.additionalCredits.map((credit, index) => (
            <div key={index} className="flex space-x-2">
              <input
                type="text"
                value={credit.role}
                onChange={(e) => {
                  const updated = [...localElements.productionCredits.additionalCredits];
                  updated[index] = { ...credit, role: e.target.value };
                  updateNestedField('productionCredits', 'additionalCredits', updated);
                }}
                className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Role"
              />
              <input
                type="text"
                value={credit.name}
                onChange={(e) => {
                  const updated = [...localElements.productionCredits.additionalCredits];
                  updated[index] = { ...credit, name: e.target.value };
                  updateNestedField('productionCredits', 'additionalCredits', updated);
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Name"
              />
              <button
                onClick={() => {
                  const updated = localElements.productionCredits.additionalCredits.filter((_, i) => i !== index);
                  updateNestedField('productionCredits', 'additionalCredits', updated);
                }}
                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const updated = [...localElements.productionCredits.additionalCredits, { role: '', name: '' }];
              updateNestedField('productionCredits', 'additionalCredits', updated);
            }}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Add Credit
          </button>
        </div>
      </div>
    </div>
  );

  const renderMetadataTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Book Metadata</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience
          </label>
          <select
            value={localElements.metadata.targetAudience}
            onChange={(e) => updateNestedField('metadata', 'targetAudience', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="General Adult">General Adult</option>
            <option value="Young Adult">Young Adult</option>
            <option value="Middle Grade">Middle Grade</option>
            <option value="Children">Children</option>
            <option value="Academic">Academic</option>
            <option value="Professional">Professional</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <input
            type="text"
            value={localElements.metadata.language}
            onChange={(e) => updateNestedField('metadata', 'language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="English"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Genre Tags
        </label>
        <input
          type="text"
          value={localElements.metadata.genre.join(', ')}
          onChange={(e) => updateNestedField('metadata', 'genre', e.target.value.split(', ').filter(g => g.trim()))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Fiction, Mystery, Thriller (comma-separated)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Keywords
        </label>
        <input
          type="text"
          value={localElements.metadata.keywords.join(', ')}
          onChange={(e) => updateNestedField('metadata', 'keywords', e.target.value.split(', ').filter(k => k.trim()))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="detective, murder, investigation (comma-separated)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          BISAC Codes
        </label>
        <input
          type="text"
          value={localElements.metadata.bisacCodes.join(', ')}
          onChange={(e) => updateNestedField('metadata', 'bisacCodes', e.target.value.split(', ').filter(b => b.trim()))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="FIC022000, FIC030000 (comma-separated)"
        />
        <p className="text-xs text-gray-500 mt-1">
          Book Industry Study Group codes for categorization
        </p>
      </div>
    </div>
  );

  const renderPreviewTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Copyright Page Preview</h3>
      
      <div className="bg-white border rounded-lg p-6 max-h-96 overflow-y-auto">
        <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800">
          {preview}
        </pre>
      </div>

      {validation && (
        <div className="space-y-2">
          {validation.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">Errors</h4>
              <ul className="text-sm text-red-700 list-disc list-inside">
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {validation.warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">Recommendations</h4>
              <ul className="text-sm text-yellow-700 list-disc list-inside">
                {validation.warnings.map((warning: string, index: number) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">
              Professional Publishing Elements
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure industry-standard publishing metadata and legal information
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b bg-gray-50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'bg-white border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'publisher' && renderPublisherTab()}
          {activeTab === 'legal' && renderLegalTab()}
          {activeTab === 'library' && renderLibraryTab()}
          {activeTab === 'credits' && renderCreditsTab()}
          {activeTab === 'metadata' && renderMetadataTab()}
          {activeTab === 'preview' && renderPreviewTab()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {validation && !validation.isValid && (
              <span className="text-red-600">
                Please fix {validation.errors.length} error(s) before saving
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              disabled={validation && !validation.isValid}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Save Elements
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalElementsEditor;